import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Observable, firstValueFrom, takeUntil } from 'rxjs';
/**
 * @customElement chancen-component-semantic-entity-multi-picker
 */
@customElement('chancen-component-semantic-entity-multi-picker')
export class ChancenComponentSemanticEntityMultiPicker<
  T extends MongoEntity,
  F extends AdornisFilter,
> extends FormField<T[]> {
  @property({ attribute: true, reflect: true, type: String }) public placeholder = '';
  @property({ attribute: true, reflect: true, type: String }) public 'placeholder-mode' = 'inline-floating';
  @property({ attribute: true, reflect: true, type: Boolean }) public controls = false;
  @property({ attribute: true, reflect: true, type: Boolean }) public clearable = true;
  @property({ attribute: false }) public filter: null | F = null;
  @property({ attribute: false }) public valueIDs = new RXController<Maybe<string[]>>(this, undefined);
  @property({ attribute: false }) public renderString: (entity: Maybe<T>, index?: number) => string = entity =>
    entity ? entity.toString() : '';

  @property({ attribute: false }) public renderItem = (item, index, value) => html`
    <x-flex ${css({ padding: `${this.spacing.sm} ${this.spacing.md}` })}>
      <x-checkbox
        disabled
        boolean-only
        .value=${this.value.value?.includes(item)}
        label=${this.renderString(item, index)}
      ></x-checkbox>
    </x-flex>
  `;

  override connectedCallback(): void {
    super.connectedCallback();

    this.valueIDs.observable.pipe(takeUntil(this.disconnected)).subscribe(async ids => {
      if (ids && ids.length > 0) {
        const operation = this._resolveID(ids);
        if (!operation) throw new Error('No _getById given');

        const entity = await operation(this._selectionSet());
        this.value.next(entity);
      } else if (ids === null && this.value.value) {
        this.value.next(null);
      }
    });
  }

  override render() {
    return html`
      <cr-flex horizontal crossaxis-center>
        <cr-dropdown-multiselection
          ${css({ maxWidth: '1600px' })}
          ?disabled=${this.disabled}
          flex
          placeholder=${this.placeholder}
          placeholder-mode=${this['placeholder-mode']}
          ?clearable=${this.clearable}
          .renderString=${this.renderString}
          .renderItem=${this.renderItem}
          .search=${this._search.bind(this)}
          .value=${this.value.value}
          @value-picked=${(e: ValueEvent<Maybe<T[]>>) => {
            this.value.next(e.detail.value);
            const event = new CustomEvent('value-picked', {
              detail: {
                value: e.detail.value,
              },
            });
            this.dispatchEvent(event);
          }}
          @cleared=${() => {
            this.value.next(null);
            const event = new CustomEvent('cleared');
            this.dispatchEvent(event);
          }}
        ></cr-dropdown-multiselection>
      </cr-flex>
    `;
  }

  protected _subscription():
    | null
    | ((skip: number, limit: number, filter: F) => (gqlFields: BaseQLSelectionSet<T>) => Observable<T[]>) {
    return null;
  }

  protected _filter(): any {
    return null;
  }

  protected _resolveID(ids: string[]): null | ((gqlFields: BaseQLSelectionSet<T>) => Promise<Maybe<T[]>>) {
    return null;
  }

  protected _selectionSet(): any {
    return {};
  }

  protected _limit(): number {
    return 20;
  }

  protected _search(search: Maybe<string>): Promise<T[]> {
    const subscribtion = this._subscription();
    if (!subscribtion) throw new Error('No subscribtion given');
    const filterClass = this._filter();
    if (!filterClass) throw new Error('No filter given');

    const filter = new filterClass({
      ...(this.filter ? this.filter.toJSON() : {}),
      search,
    });

    return firstValueFrom(subscribtion(0, this._limit(), filter)(this._selectionSet()));
  }
}
